import { useState } from "react";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { PicoBubble, usePicoToast } from "../../../components/PicoBubble";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useDensity } from "../../../store/density";
import { useT } from "../../../i18n";
import { AVATARS, avatarEmoji, useMyAvatar } from "../../pico";
import type { AvatarKey } from "../../pico";
import { useSession } from "../../../store/session/SessionProvider";

/**
 * FS-01-03 아바타 선택 — 카탈로그 아바타(피코 친구 프리셋) 그리드 · 미리보기 · 저장(SCR v0.6).
 * 자유 프로필 필드 없음(DR-02) — 별명은 선생님이 정한다(표시만). 버튼은 SE 64px 이상(DR-03).
 * 상태: 기본 · 저장됨.
 */
export function AvatarScreen() {
  const t = useT();
  const { density } = useDensity();
  const state = useSession();
  const name = state.status === "ready" ? state.session.displayName : "";
  const { avatar, save } = useMyAvatar();
  const toast = usePicoToast();
  const [pick, setPick] = useState<AvatarKey | null>(null);
  const [saved, setSaved] = useState(false);
  const chosen = pick ?? avatar;
  useTopBar({ icon: avatarEmoji(chosen), title: t("student.avatar.title") });

  return (
    <ScreenRoot id="FS-01-03" title={t("student.avatar.title")} hideTitle>
      <div className={`pf-pane pf-pane--hero pf-avatar pf-avatar--${density}`}>
        <PicoBubble
          text={saved ? t("student.avatar.saved") : t("student.avatar.pico")}
          live={saved}
        />
        <div className="pf-avatar__body">
          <figure className="pf-avatar__preview" data-testid="avatar-preview">
            <span className="pf-avatar__big" aria-hidden="true">
              {avatarEmoji(chosen)}
            </span>
            <figcaption>
              <b>{name}</b>
              <span className="pf-muted">{t(`student.avatar.names.${chosen}`)}</span>
            </figcaption>
          </figure>
          <div className="pf-avatar__pick">
            <div
              className="pf-avatar__grid"
              role="radiogroup"
              aria-label={t("student.avatar.grid")}
              data-testid="avatar-grid"
            >
              {AVATARS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="radio"
                  aria-checked={chosen === a.key}
                  aria-label={t(`student.avatar.names.${a.key}`)}
                  className={`pf-avatar__cell${chosen === a.key ? " pf-avatar__cell--on" : ""}`}
                  data-avatar={a.key}
                  onClick={() => {
                    setPick(a.key);
                    setSaved(false);
                  }}
                >
                  <span aria-hidden="true">{a.emoji}</span>
                </button>
              ))}
            </div>
            <p className="pf-muted">{t("student.avatar.note")}</p>
          </div>
        </div>
        <div className="pf-row pf-avatar__actions">
          <ButtonLink to="/home" variant="ghost" icon="🏠">
            {t("common.toEntrance")}
          </ButtonLink>
          <Button
            variant="accent"
            icon={saved ? "✓" : "💾"}
            disabled={saved || chosen === avatar}
            onClick={() =>
              void save(chosen).then((ok) => {
                setSaved(ok);
                if (!ok) toast.show("avatar-save", t("student.avatar.saveLater"));
              })
            }
            data-testid="avatar-save"
          >
            {saved ? t("student.avatar.savedChip") : t("student.avatar.save")}
          </Button>
        </div>
      </div>
    </ScreenRoot>
  );
}
